/**
 * oneBUTTON — IGS EPC Sandbox Calculator modal (parity with Angular igs-epc-calc).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Input, InputNumber, Slider, Spin } from 'antd';
import CurrencyInput from 'react-currency-input-field';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import quoteServices, { type CalculateIgsEpcPayload } from '../../../../services/oneButtonServices/quoteServices';
import type { IGSEpcCalcModalProps, IGSEpcDropdownItem, IGSEpcSliderField } from './IGSEpcCalcModal.types';
import './oneButton-css/IGSEpcCalcModal.css';

function toNumber(v: unknown): number {
    const n = typeof v === 'number' ? v : Number(String(v ?? '').replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : 0;
}

function moneyMark(v: number): string {
    if (!Number.isFinite(v)) return '$0';
    return `$${v}`;
}

function fmtRate(n: number): string {
    if (!Number.isFinite(n)) return '0';
    return n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}

function formatRateReadout(kind: string, n: number): string {
    const v = Number.isFinite(n) ? n : 0;
    if (kind === 'Monthly Payment') {
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
    }
    // Angular shows small Solar Rate values (e.g. 0.123) without rounding to 2dp,
    // otherwise they look like "0.00" and appear missing.
    if (v === 0) return '0.00';
    return fmtRate(v);
}

function parseAmountDisplayString(display: string): number {
    const n = parseFloat(display.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
}



/**
 * Angular passes `this.solar?.contract_Type` into Excel cell F4. RetrievePriceQuote often leaves `Solar.Finance_Method_Name`
 * empty while `QuoteVersion[].Quote_FinanceMethod` still has the method (e.g. `"Lease"`).
 */
function trimFinanceMethod(v: unknown): string {
    if (v == null) return '';
    const s = String(v).trim();
    return s;
}

function resolveFinanceMethodForIgsEpc(quoteDetails: unknown, financeMethodFromProps: string | null | undefined, partnerDisplayName?: string): string {
    if (partnerDisplayName && partnerDisplayName.trim().toLowerCase() === 'igs') {
        return 'Lease';
    }

    const fromProps = trimFinanceMethod(financeMethodFromProps);
    if (fromProps && fromProps !== '0') return fromProps;

    const q = quoteDetails as Record<string, unknown> | null | undefined;
    if (!q) return 'Lease';

    const solar = (q.Solar ?? {}) as Record<string, unknown>;
    const fromSolar = trimFinanceMethod(
        solar.Finance_Method_Name ?? solar.contract_Type ?? solar.ContractType ?? solar.Finance_Method ?? solar.contract_type,
    );
    if (fromSolar && fromSolar !== '0') return fromSolar;

    const versions = Array.isArray(q.QuoteVersion) ? q.QuoteVersion : [];
    const selected = versions.find(
        (row: unknown) =>
            row &&
            typeof row === 'object' &&
            ((row as Record<string, unknown>).Selected === true ||
                String((row as Record<string, unknown>).Selected).toLowerCase() === 'true'),
    );
    const row = (selected ?? versions[0]) as Record<string, unknown> | undefined;
    const fromQuoteVersion = trimFinanceMethod(row?.Quote_FinanceMethod ?? row?.QuoteFinanceMethod);
    if (fromQuoteVersion && fromQuoteVersion !== '0') return fromQuoteVersion;

    const syncList = Array.isArray(q.SyncProductInfo) ? q.SyncProductInfo : [];
    const syncSolar = syncList.find(
        (s: unknown) =>
            s &&
            typeof s === 'object' &&
            String((s as Record<string, unknown>).Product_Type_Name ?? '').toLowerCase() === 'solar',
    ) as Record<string, unknown> | undefined;
    const fromSync = trimFinanceMethod(syncSolar?.FinanceMethodName);
    if (fromSync && fromSync !== '0') return fromSync;

    const reqMethod = trimFinanceMethod(q.Requested_Finance_Method);
    if (reqMethod && reqMethod !== '0') return reqMethod;

    return 'Lease';
}

/** Aligns quote labels such as `Lease - Solar Rate` with dropdown text `Solar Rate` from CascadingDropdowns. */
function normalizePricingMethodLabel(label: string): string {
    const t = label.trim();
    const i = t.lastIndexOf(' - ');
    return i >= 0 ? t.slice(i + 3).trim() : t;
}

function selectedQuoteVersionRow(quoteDetails: unknown): Record<string, unknown> | undefined {
    const q = quoteDetails as Record<string, unknown> | undefined;
    const versions = Array.isArray(q?.QuoteVersion) ? q.QuoteVersion : [];
    const selected = versions.find(
        (row: unknown) =>
            row &&
            typeof row === 'object' &&
            ((row as Record<string, unknown>).Selected === true ||
                String((row as Record<string, unknown>).Selected).toLowerCase() === 'true'),
    );
    return (selected ?? versions[0]) as Record<string, unknown> | undefined;
}

const getSliderCssVars = (style?: string): React.CSSProperties => {
    if (!style) return {};
    return style.split(';').reduce((acc, part) => {
        const [key, ...valueParts] = part.split(':');
        const value = valueParts.join(':').trim();
        if (key?.trim() && value) {
            (acc as Record<string, string>)[key.trim()] = value;
        }
        return acc;
    }, {} as React.CSSProperties);
};

export const IGSEpcCalcModal: React.FC<IGSEpcCalcModalProps> = ({
    open,
    onClose,
    theme,
    quoteDetails,
    financeMethodId,
    partnerDisplayName = 'IGS',
    opportunityId,
}) => {
    const notifBg = useMemo(
        () => (theme === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.4)'),
        [theme],
    );

    const [loading, setLoading] = useState(false);
    const [calcLoading, setCalcLoading] = useState(false);
    const [error, setError] = useState<string>('');

    const [escalators, setEscalators] = useState<IGSEpcDropdownItem[]>([]);
    const [pricingMethods, setPricingMethods] = useState<IGSEpcDropdownItem[]>([]);

    const [selectedEscalator, setSelectedEscalator] = useState<string>('');
    const [bonusItc, setBonusItc] = useState(false);
    // Angular uses ngx-currency on text inputs (allows empty string).
    const [annualProduction, setAnnualProduction] = useState<string>('0.00');
    const [systemSize, setSystemSize] = useState<string>('0.00');
    // Keep Select values strictly as strings so AntD selection always binds correctly.
    const [priceSystem, setPriceSystem] = useState<string>('');
    // Angular clears rate to '' on Pricing Method change; keep `null` to represent empty.
    const [rate, setRate] = useState<number | null>(null);
    const [financePartnerContractType, setFinancePartnerContractType] = useState<string>('');
    const [sliderField, setSliderField] = useState<IGSEpcSliderField | null>(null);

    const [sandboxOpen, setSandboxOpen] = useState(false);
    const [sandboxData, setSandboxData] = useState<any>(null);

    const annualProductionInputRef = useRef<HTMLInputElement | null>(null);
    const systemSizeInputRef = useRef<HTMLInputElement | null>(null);
    const draftRef = useRef<Record<string, string>>({});

    const regionLabel = useMemo(() => {
        const st = quoteDetails?.InstallationState ?? '';
        const ut = quoteDetails?.UtilityCompanyName ?? '';
        return `${st} - ${ut}`.trim();
    }, [quoteDetails]);

    const resolvedFinanceMethodId = useMemo(
        () => resolveFinanceMethodForIgsEpc(quoteDetails, financeMethodId, partnerDisplayName),
        [quoteDetails, financeMethodId, partnerDisplayName],
    );

    const selectedPricingMethod = useMemo(() => {
        return pricingMethods.find((p) => String(p.Value) === String(priceSystem)) ?? null;
    }, [pricingMethods, priceSystem]);

    /** Normalized pricing mode (`Lease - Solar Rate` → `Solar Rate`), used for slider bounds and API payload fields. */
    const selectedPricingKind = useMemo(
        () => normalizePricingMethodLabel(selectedPricingMethod?.Text ?? ''),
        [selectedPricingMethod?.Text],
    );

    /** Excel-backed slider config when present; otherwise defaults so Rate is always controlled with a Slider. */
    const rateSliderBounds = useMemo(() => {
        if (sliderField) {
            return {
                min: sliderField.nzMin,
                max: sliderField.nzMax,
                step: sliderField.nzStep,
                marks: sliderField.nzMarks,
                defaultValue: sliderField.nzValue,
                style: getSliderCssVars(sliderField.nzStyle),
            };
        }
        if (selectedPricingKind === 'Monthly Payment') {
            return { min: 0, max: 5000, step: 0.01, marks: {} as Record<string, string>, defaultValue: 0 };
        }
        if (selectedPricingKind === 'PPW') {
            return { min: 0, max: 5, step: 0.01, marks: {} as Record<string, string>, defaultValue: 0 };
        }
        return { min: 0, max: 1, step: 0.001, marks: {} as Record<string, string>, defaultValue: 0 };
    }, [sliderField, selectedPricingKind]);

    const rateSliderDisplayValue = useMemo(() => {
        const d = rateSliderBounds.defaultValue;
        const raw = rate ?? d;
        const n = Number.isFinite(raw) ? raw : d;
        return Math.min(Math.max(n, rateSliderBounds.min), rateSliderBounds.max);
    }, [rate, rateSliderBounds]);

    /** Avoid passing empty `marks` — Ant Design may reserve odd vertical space. */
    const rateSliderMarks = useMemo(() => {
        const m = rateSliderBounds.marks;
        return Object.keys(m).length > 0 ? m : undefined;
    }, [rateSliderBounds.marks]);

    const formatRateTooltip = useCallback(
        (val?: number) => {
            const v = Number(val ?? 0);
            if (selectedPricingKind === 'Monthly Payment') {
                return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)}`;
            }
            return `$${fmtRate(v)}`;
        },
        [selectedPricingKind],
    );

    const fetchSliderDetails = useCallback(
        async (pricingText: string, escalatorValue: string) => {
            const installationState = String(quoteDetails?.InstallationState ?? '').trim();
            const excelUtilityCompanyName = String(quoteDetails?.ExcelUtilityCompanyName ?? '').trim();
            const fm = resolvedFinanceMethodId.trim();
            if (!installationState || !excelUtilityCompanyName || !fm || !pricingText) return;

            const pricingKind = normalizePricingMethodLabel(pricingText);
            if (pricingKind !== 'Solar Rate') {
                setSliderField(null);
                setRate(null);
                return;
            }

            setLoading(true);
            setError('');
            try {
                // Angular `retiveSliderDetails`: K11=json slider config, U4=contract type
                const response: any[] | null = await masterServices.cascadingDropDown({
                    ExcelFileName: 'Sunnova Financial Data',
                    SheetName: 'SVA Financing Options',
                    InputValues: [
                        { CellNumber: 'B4', CellValue: installationState, Type: 'String' },
                        { CellNumber: 'C4', CellValue: 'S', Type: 'String' },
                        { CellNumber: 'D4', CellValue: partnerDisplayName, Type: 'String' },
                        { CellNumber: 'E8', CellValue: excelUtilityCompanyName, Type: 'String' },
                        { CellNumber: 'F4', CellValue: fm, Type: 'String' },
                        { CellNumber: 'K4', CellValue: pricingText, Type: 'String' },
                        { CellNumber: 'M4', CellValue: escalatorValue, Type: 'String' },
                    ],
                    OutputCells: ['K11', 'U4'],
                });

                const raw = response?.[0]?.CellValue ? JSON.parse(response[0].CellValue) : null;
                const contractType = String(response?.[1]?.CellValue ?? '').trim();
                setFinancePartnerContractType(contractType);

                if (raw) {
                    let min = 0, max = 0, start = 0;
                    if (pricingKind === 'PPW') {
                        min = toNumber(raw.IGSRateMin);
                        max = toNumber(raw.IGSRateMax);
                        start = toNumber(raw.IGSRateStart);
                    } else if (raw.Output) {
                        min = toNumber(raw.Output[`${escalatorValue} Rate Min`]);
                        max = toNumber(raw.Output[`${escalatorValue} Rate Max`]);
                        start = toNumber(raw.Output[`${escalatorValue} Rate Start`]);
                    }

                    const marks: Record<string, any> = {};
                    let styleStr = '';

                    if (min && max && start) {
                        const splitPoint = max > min ? ((start - min) / (max - min)) * 100 : 0;
                        const yellow = splitPoint;
                        const green = 100 - yellow;
                        
                        styleStr = `--yellow-area:${yellow}%;--green-area:${green}%;--slider-color:linear-gradient(to right, yellow 0%, yellow ${yellow}%, lightgreen ${yellow}%, lightgreen 100%);--slider-track-color:transparent;`;

                        marks[min] = moneyMark(min);
                        marks[max] = moneyMark(max);
                        marks[start] = {
                            style: { transform: 'translateX(-100%)', whiteSpace: 'nowrap' },
                            label: moneyMark(start)
                        };
                    } else {
                        if (min) marks[min] = moneyMark(min);
                        if (max) marks[max] = moneyMark(max);
                    }

                    setSliderField({
                        nzMin: min,
                        nzMax: max,
                        nzValue: start,
                        nzStep: pricingKind === 'PPW' ? 0.01 : 0.001,
                        nzMarks: marks,
                        nzStyle: styleStr
                    });
                    setRate(start);
                } else {
                    setSliderField(null);
                    setRate(null);
                }
            } catch (e) {
                setError('Failed to load slider details.');
                setSliderField(null);
            } finally {
                setLoading(false);
            }
        },
        [quoteDetails, resolvedFinanceMethodId, partnerDisplayName],
    );

    const fetchPricingOptions = useCallback(async () => {
        const installationState = String(quoteDetails?.InstallationState ?? '').trim();
        const excelUtilityCompanyName = String(quoteDetails?.ExcelUtilityCompanyName ?? '').trim();
        const fm = resolvedFinanceMethodId.trim();
        if (!installationState || !excelUtilityCompanyName || !fm) {
            setEscalators([]);
            setPricingMethods([]);
            return;
        }

        setLoading(true);
        setError('');
        try {
            // Angular EPC modal is opened AFTER Solar form already loaded these.
            // We must do the same CascadingDropdowns call on open to get PricingMethods + Escalators.
            const response: any[] | null = await masterServices.cascadingDropDown({
                ExcelFileName: 'Sunnova Financial Data',
                SheetName: 'SVA Financing Options',
                InputValues: [
                    { Type: 'String', CellNumber: 'B4', CellValue: installationState },
                    { Type: 'String', CellNumber: 'C4', CellValue: 'S' },
                    { Type: 'String', CellNumber: 'D4', CellValue: partnerDisplayName },
                    { Type: 'String', CellNumber: 'E8', CellValue: excelUtilityCompanyName },
                    { Type: 'String', CellNumber: 'F4', CellValue: fm },
                    { Type: 'String', CellNumber: 'K4', CellValue: '' },
                    { Type: 'String', CellNumber: 'M4', CellValue: '' },
                ],
                OutputCells: ['H4', 'L4', 'I4'],
            });

            const pricingLabels = String(response?.[0]?.CellValue ?? '').split('|').map((s) => s.trim()).filter(Boolean);
            const escalatorLabels = String(response?.[1]?.CellValue ?? '').split('|').map((s) => s.trim()).filter(Boolean);
            const pricingValues = String(response?.[2]?.CellValue ?? '').split('|').map((s) => s.trim());

            const pm: IGSEpcDropdownItem[] = pricingLabels.map((text, i) => ({
                Text: text,
                Value: String(i),
                PricingValue: (pricingValues[i] ? pricingValues[i] : text).trim(),
            }));
            const esc: IGSEpcDropdownItem[] = escalatorLabels.map((t) => ({ Text: t, Value: t }));

            setPricingMethods(pm);
            setEscalators(esc);

            // Seed defaults similar to Angular: use current quoteDetails values if present, otherwise pick first option.
            const qvRow = selectedQuoteVersionRow(quoteDetails);
            // Bind the first item in the dropdown list
            const escValue = esc.length > 0 ? String(esc[0].Value) : '';
            if (escValue) setSelectedEscalator(escValue);

            const desiredPricingText =
                quoteDetails?.Solar?.PricingMethod != null && String(quoteDetails.Solar.PricingMethod).trim() !== ''
                    ? String(quoteDetails.Solar.PricingMethod).trim()
                    : quoteDetails?.Quote_PricingMethod != null && String(quoteDetails.Quote_PricingMethod).trim() !== ''
                      ? String(quoteDetails.Quote_PricingMethod).trim()
                      : qvRow?.Quote_PricingMethod != null && String(qvRow.Quote_PricingMethod).trim() !== ''
                        ? String(qvRow.Quote_PricingMethod).trim()
                        : '';
            const pmMatch = desiredPricingText
                ? pm.find((p) => {
                      const a = normalizePricingMethodLabel(String(p.Text)).toLowerCase();
                      const b = normalizePricingMethodLabel(desiredPricingText).toLowerCase();
                      return a === b;
                  })
                : null;
            const pmValue = pmMatch ? String(pmMatch.Value) : pm.length > 0 ? String(pm[0].Value) : '';
            if (pmValue) setPriceSystem(pmValue);

            // IMPORTANT: Angular immediately triggers `retiveSliderDetails()` during init via `updatePricingMethord()`.
            const pricingText = pm.find((p) => String(p.Value) === pmValue)?.Text ?? '';
            if (pricingText) {
                // Clear current rate like Angular does.
                setRate(null);
                setSliderField(null);
                await fetchSliderDetails(pricingText, escValue);
            }
        } catch (e) {
            setError('Failed to load pricing methods and escalators.');
            setPricingMethods([]);
            setEscalators([]);
        } finally {
            setLoading(false);
        }
    }, [quoteDetails, resolvedFinanceMethodId, partnerDisplayName, fetchSliderDetails]);

    useEffect(() => {
        if (!open) return;
        void fetchPricingOptions();
    }, [open, fetchPricingOptions]);

    useEffect(() => {
        if (!open) return;
        // Match Angular: inputs initialize as 0.00 (mask formatting), not auto-prefilled.
        setAnnualProduction('0.00');
        setSystemSize('0.00');
        setRate(null);
        draftRef.current = {};
    }, [open]);

    const validate = (): boolean => {
        const ss = toNumber(systemSize);
        const ap = toNumber(annualProduction);
        if (ss <= 0 && ap <= 0) {
            setError('System Size and Annual Production must be greater than 0.');
            return false;
        }
        if (ss <= 0) {
            setError('System Size must be greater than 0.');
            return false;
        }
        if (ap <= 0) {
            setError('Annual Production must be greater than 0.');
            return false;
        }
        if (toNumber(rateSliderDisplayValue) === 0) {
            setError('Rate or Monthly Payment is required.');
            return false;
        }
        return true;
    };

    const onClear = () => {
        setError('');
        setBonusItc(false);
        setAnnualProduction('0.00');
        setSystemSize('0.00');
        setRate(null);
        draftRef.current = {};
        if (pricingMethods.length > 0) setPriceSystem(String(pricingMethods[0].Value));
        if (escalators.length > 0) setSelectedEscalator(String(escalators[0].Value));
    };

    const onCalculate = async () => {
        setError('');
        if (!validate()) return;

        const payload: CalculateIgsEpcPayload = {
            opportunityId: Number(opportunityId ?? 0),
            escalator: selectedEscalator ? Number(selectedEscalator) : 0,
            annualProduction: toNumber(annualProduction),
            systemSize: toNumber(systemSize),
            ecChecked: !!bonusItc,
            rate: selectedPricingKind === 'Solar Rate' ? toNumber(rateSliderDisplayValue) : 0,
            monthlyPayment:
                selectedPricingKind === 'Monthly Payment' ? Number(toNumber(rateSliderDisplayValue).toFixed(2)) : 0,
            stateCode: String(quoteDetails?.InstallationState ?? ''),
            Finance_partner_contract_Type: String(financePartnerContractType ?? ''),
        };

        setCalcLoading(true);
        try {
            const res = await quoteServices.calculateIgsEpc(payload);
            setSandboxData({
                calcData: res,
                formData: {
                    escalator: selectedEscalator,
                    bonusITCAdders: bonusItc,
                    annualProduction,
                    systemSize,
                    rate: rateSliderDisplayValue,
                },
                quoteData: quoteDetails,
                selectedPricingMethodName: selectedPricingMethod?.Text ?? '',
            });
            setSandboxOpen(true);
        } catch (e: any) {
            setError(e?.response?.data?.Message || e?.message || 'Failed to retrieve financial data from IGS. Please try again.');
        } finally {
            setCalcLoading(false);
        }
    };

    const footer = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center', width: '100%' }}>
            <span />
            <Button onClick={onClear} disabled={loading || calcLoading}>
                Clear
            </Button>
            <Button type="primary" onClick={onCalculate} loading={calcLoading} disabled={loading || calcLoading}>
                Calculate
            </Button>
        </div>
    );

    // ----- Handlers (avoid inline JSX logic) -----
    const handleEscalatorChange = useCallback(
        (v: unknown) => {
            const esc = String(v);
            setSelectedEscalator(esc);
            void fetchSliderDetails(selectedPricingMethod?.Text ?? '', esc);
        },
        [fetchSliderDetails, selectedPricingMethod],
    );

    const handlePriceSystemChange = useCallback(
        (v: unknown) => {
            const next = String(v);
            setPriceSystem(next);
            setRate(null);
            setSliderField(null);
            const pricingText = pricingMethods.find((p) => String(p.Value) === next)?.Text ?? '';
            void fetchSliderDetails(pricingText, selectedEscalator);
        },
        [fetchSliderDetails, pricingMethods, selectedEscalator],
    );

    const handleRateSliderChange = useCallback((v: number | number[]) => {
        setRate(Number(v));
    }, []);

    return (
        <>
            <DashboardModal
                title="IGS EPC Sandbox Calculator"
                open={open}
                onClose={onClose}
                footer={footer}
                width="min(820px, 96vw)"
                theme={theme}
                maskClosable={false}
                destroyOnClose
                className="obd-dashboard-modal--igs-epc"
                rootClassName={`obd-theme-${theme}`}
                zIndex={10070}
                loading={loading}
                loadingMessage="Loading…"
            >
                <div className="obd-igs-epc">
                    {calcLoading && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 10,
                                background: notifBg,
                                backdropFilter: 'blur(1px)',
                                zIndex: 10,
                                borderRadius: 16,
                            }}
                        >
                            <Spin />
                            <span style={{ fontWeight: 500, color: theme === 'dark' ? 'rgba(248, 250, 252, 0.98)' : '#1e293b' }}>
                                Calculating…
                            </span>
                        </div>
                    )}

                    <div className="obd-igs-epc__card">
                        <div className="obd-igs-epc__grid-2">
                            <div>
                                <div className="obd-igs-epc__label">Region</div>
                                <Input value={regionLabel} disabled />
                            </div>
                            <div>
                                <div className="obd-igs-epc__label">Escalator (%)</div>
                                <OBDSelect
                                    value={selectedEscalator || undefined}
                                    onChange={handleEscalatorChange}
                                    options={escalators.map((e) => ({ value: e.Value, label: e.Text }))}
                                    style={{ width: '100%' }}
                                    popupClassName={`obd-select-dropdown obd-theme-${theme}`}
                                />
                            </div>
                        </div>

                        <div>
                            <div className="obd-igs-epc__label">Bonus ITC Adders</div>
                            <Checkbox checked={bonusItc} onChange={(e) => setBonusItc(e.target.checked)}>
                                Energy Communities (10%)
                            </Checkbox>
                        </div>
                    </div>

                    <div className="obd-igs-epc__card">
                        <div className="obd-igs-epc__section-title">System Specifications</div>
                        <div className="obd-igs-epc__grid-2">
                            <div>
                                <div className="obd-igs-epc__label">Annual Production (Wh)</div>
                                <CurrencyInput
                                    ref={annualProductionInputRef}
                                    className="ant-input"
                                    style={{ width: '100%' }}
                                    value={
                                        draftRef.current['annualProduction'] ??
                                        (annualProduction === undefined || annualProduction === null || String(annualProduction).trim() === ''
                                            ? ''
                                            : String(annualProduction))
                                    }
                                    onValueChange={(val) => {
                                        const v = val ?? '';
                                        const num = v ? parseAmountDisplayString(v) : 0;
                                        if (!v || num <= 1000000000) {
                                            draftRef.current['annualProduction'] = v;
                                            setAnnualProduction(v);
                                        }
                                    }}
                                    onBlur={() => {
                                        const raw = draftRef.current['annualProduction'] ?? String(annualProduction ?? '');
                                        const n = parseAmountDisplayString(raw);
                                        setAnnualProduction(n.toFixed(2));
                                        delete draftRef.current['annualProduction'];
                                    }}
                                    decimalsLimit={2}
                                    allowNegativeValue={false}
                                    autoComplete="off"
                                />
                            </div>
                            <div>
                                <div className="obd-igs-epc__label">System Size (kW)</div>
                                <CurrencyInput
                                    ref={systemSizeInputRef}
                                    className="ant-input"
                                    style={{ width: '100%' }}
                                    value={
                                        draftRef.current['systemSize'] ??
                                        (systemSize === undefined || systemSize === null || String(systemSize).trim() === ''
                                            ? ''
                                            : String(systemSize))
                                    }
                                    onValueChange={(val) => {
                                        const v = val ?? '';
                                        const num = v ? parseAmountDisplayString(v) : 0;
                                        if (!v || num <= 1000000000) {
                                            draftRef.current['systemSize'] = v;
                                            setSystemSize(v);
                                        }
                                    }}
                                    onBlur={() => {
                                        const raw = draftRef.current['systemSize'] ?? String(systemSize ?? '');
                                        const n = parseAmountDisplayString(raw);
                                        setSystemSize(n.toFixed(2));
                                        delete draftRef.current['systemSize'];
                                    }}
                                    decimalsLimit={2}
                                    allowNegativeValue={false}
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="obd-igs-epc__card">
                        <div className="obd-igs-epc__section-title">Pricing</div>
                        <div className="obd-igs-epc__grid-2">
                            <div>
                                <div className="obd-igs-epc__label">Price System By</div>
                                <OBDSelect
                                    value={priceSystem === '' ? undefined : priceSystem}
                                    onChange={handlePriceSystemChange}
                                    options={pricingMethods.map((p) => ({ value: String(p.Value), label: p.Text }))}
                                    style={{ width: '100%' }}
                                    popupClassName={`obd-select-dropdown obd-theme-${theme}`}
                                />
                            </div>
                            <div>
                                <div className="obd-igs-epc__label">
                                    {selectedPricingKind === 'Monthly Payment'
                                        ? '\u00A0'
                                        : selectedPricingKind === 'Solar Rate'
                                          ? 'Rate'
                                          : selectedPricingMethod?.Text || 'Rate'}
                                </div>
                                {selectedPricingKind === 'Monthly Payment' ? (
                                    <CurrencyInput
                                        className="ant-input"
                                        style={{ width: '100%' }}
                                        value={
                                            draftRef.current['rate'] ??
                                            (rate === null || rate === undefined ? '0.00' : Number(rate).toFixed(2))
                                        }
                                        onValueChange={(val) => {
                                            const v = val ?? '';
                                            draftRef.current['rate'] = v;
                                            const num = v ? parseAmountDisplayString(v) : 0;
                                            setRate(num);
                                        }}
                                        onBlur={() => {
                                            const raw = draftRef.current['rate'] ?? String(rate ?? '');
                                            const n = parseAmountDisplayString(raw);
                                            setRate(Number(n.toFixed(2)));
                                            delete draftRef.current['rate'];
                                        }}
                                        decimalsLimit={2}
                                        allowNegativeValue={false}
                                        autoComplete="off"
                                    />
                                ) : (
                                    <div className="obd-igs-epc__slider-wrap">
                                        <Slider
                                            className="obd-igs-epc__slider"
                                            included
                                            min={rateSliderBounds.min}
                                            max={rateSliderBounds.max}
                                            step={rateSliderBounds.step}
                                            marks={rateSliderMarks}
                                            value={rateSliderDisplayValue}
                                            onChange={handleRateSliderChange}
                                            style={rateSliderBounds.style ? rateSliderBounds.style : undefined}
                                            tooltip={{
                                                formatter: (v) => formatRateTooltip(Number(v ?? 0)),
                                                getPopupContainer: (n) => (n?.parentElement != null ? n.parentElement : document.body),
                                                placement: 'top',
                                            }}
                                        />
                                        {/* Min/max: either Ant `marks` (Excel) or this row — never both or labels duplicate. */}
                                        {!rateSliderMarks && (
                                            <div className="obd-igs-epc__slider-minmax" aria-hidden>
                                                <span>{formatRateTooltip(rateSliderBounds.min)}</span>
                                                <span>{formatRateTooltip(rateSliderBounds.max)}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="obd-igs-epc__rate-readout">
                            <span className="obd-igs-epc__rate-label">$:</span>
                            <span className="obd-igs-epc__rate-value">
                                {formatRateReadout(selectedPricingKind, toNumber(rate ?? rateSliderDisplayValue))}
                            </span>
                        </div>

                        {error && <Alert message={error} type="error" showIcon />}
                    </div>
                </div>
            </DashboardModal>

            <DashboardModal
                title="Sandbox Quote"
                open={sandboxOpen}
                onClose={() => setSandboxOpen(false)}
                footer={null}
                width={600}
                theme={theme}
                maskClosable={false}
                destroyOnClose
                rootClassName={`obd-theme-${theme}`}
                zIndex={10080}
            >
                <div style={{ padding: 16 }}>
                    {sandboxData ? (
                        <div style={{ display: 'grid', gap: 10 }}>
                            <div style={{ padding: 12, borderRadius: 12, background: theme === 'dark' ? 'rgba(148, 163, 184, 0.08)' : 'rgba(15, 23, 42, 0.04)' }}>
                                <div style={{ fontSize: 12, opacity: 0.8 }}>Total Monthly Payment</div>
                                <div style={{ fontSize: 18, fontWeight: 800 }}>
                                    {sandboxData?.calcData?.totalMonthlyPayment != null ? `$${sandboxData.calcData.totalMonthlyPayment}` : '—'}
                                </div>
                            </div>
                            <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
                                <div><strong>Product</strong>: {sandboxData?.calcData?.productName ?? '—'}</div>
                                <div><strong>Region</strong>: {regionLabel}</div>
                                <div><strong>Escalator</strong>: {sandboxData?.formData?.escalator ?? '0'}%</div>
                                <div><strong>ITC Percent</strong>: {sandboxData?.calcData?.itcPercent ?? '—'}%{sandboxData?.formData?.bonusITCAdders ? ' - Energy Communities (10%)' : ''}</div>
                                <div><strong>Annual Production</strong>: {sandboxData?.formData?.annualProduction ?? '0'} Wh</div>
                                <div><strong>System Size</strong>: {sandboxData?.formData?.systemSize ?? '0'} kW</div>
                                <div>
                                    <strong>
                                        {normalizePricingMethodLabel(String(sandboxData?.selectedPricingMethodName ?? '')) === 'Solar Rate'
                                            ? 'Rate'
                                            : sandboxData?.selectedPricingMethodName ?? 'Rate'}
                                    </strong>
                                    : ${sandboxData?.formData?.rate ?? 0}
                                    {normalizePricingMethodLabel(String(sandboxData?.selectedPricingMethodName ?? '')) === 'Solar Rate'
                                        ? ' /kWh'
                                        : ''}
                                </div>
                                <div><strong>Total EPC</strong>: {sandboxData?.calcData?.totalEPC != null ? `$${sandboxData.calcData.totalEPC}` : '—'}</div>
                            </div>
                        </div>
                    ) : (
                        <div>No data</div>
                    )}
                </div>
            </DashboardModal>
        </>
    );
};

export default IGSEpcCalcModal;

